export const LISTENER_COUNT_REFRESH_INTERVAL_MS = 5 * 60 * 1000

const LISTENER_COUNT_STALE_THRESHOLD_MS = 10 * 60 * 1000

export function resolveListenerCountStale(
  serverValue: boolean | null | undefined,
  lastUpdated: string | null | undefined,
  now: number = Date.now(),
): boolean | null {
  if (typeof serverValue === 'boolean') {
    return serverValue
  }

  if (!lastUpdated) {
    return null
  }

  const parsed = Date.parse(lastUpdated)
  if (Number.isNaN(parsed)) {
    return null
  }

  return now - parsed >= LISTENER_COUNT_STALE_THRESHOLD_MS
}

export function formatListenerCountRelativeTime(
  input: string | number | Date,
  now: number = Date.now(),
): string {
  let timestamp: number | null = null

  if (typeof input === 'number') {
    timestamp = Number.isFinite(input) ? input : null
  } else if (input instanceof Date) {
    const value = input.getTime()
    timestamp = Number.isNaN(value) ? null : value
  } else if (typeof input === 'string') {
    const parsed = Date.parse(input)
    timestamp = Number.isNaN(parsed) ? null : parsed
  }

  if (timestamp === null) {
    return 'recently'
  }

  const diffSeconds = Math.round((timestamp - now) / 1000)
  const absSeconds = Math.abs(diffSeconds)
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

  const units: Array<{ unit: Intl.RelativeTimeFormatUnit; limit: number; divisor: number }> = [
    { unit: 'second', limit: 60, divisor: 1 },
    { unit: 'minute', limit: 3600, divisor: 60 },
    { unit: 'hour', limit: 86400, divisor: 3600 },
    { unit: 'day', limit: 604800, divisor: 86400 },
    { unit: 'week', limit: 2629800, divisor: 604800 },
    { unit: 'month', limit: 31557600, divisor: 2629800 },
    { unit: 'year', limit: Infinity, divisor: 31557600 },
  ]

  for (const { unit, limit, divisor } of units) {
    if (absSeconds < limit) {
      const value = Math.round(diffSeconds / divisor)
      return formatter.format(value === 0 ? 0 : value, unit)
    }
  }

  return formatter.format(Math.round(diffSeconds / 31557600), 'year')
}

export function parseListenerPlainText(input: string): number | null {
  if (!input) {
    return null
  }

  const normalized = input.replace(/\r\n/g, '\n').trim()
  if (!normalized) {
    return null
  }

  const lines = normalized
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)

  if (lines.length === 0) {
    return null
  }

  const lastLine = lines[lines.length - 1]
  const [left] = lastLine.split(':', 2)
  const trimmed = (left ?? '').trim()

  if (!trimmed || !/^-?\d+$/.test(trimmed)) {
    return null
  }

  const parsed = Number.parseInt(trimmed, 10)
  return Number.isNaN(parsed) ? null : parsed
}
